import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../common/Navbar';
import { 
  Search, 
  Filter, 
  RefreshCw,
  X,
  ChevronLeft,
  ChevronRight,
  Users,
  Calendar,
  DollarSign,
  Eye,
  AlertTriangle,
  Download,
  User as UserIcon
} from 'lucide-react';
import { leadService } from '../../services/api';

export default function NegotiationLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all'); // all, my, stale, week
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  const [filters, setFilters] = useState({
    consultant_id: '',
    sector_id: '',
    country_id: ''
  });
  
  const [lookups, setLookups] = useState({
    consultants: [],
    sectors: [],
    countries: []
  });

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadLookups();
    loadLeads();
  }, [pagination.page, quickFilter]);

  const loadLookups = async () => {
    try {
      const response = await leadService.getLookups();
      const data = response?.data?.data || response?.data || {};
      
      setLookups({
        consultants: data.consultants || [],
        sectors: data.sectors || [],
        countries: data.countries || []
      });
    } catch (error) {
      console.error('Error loading lookups:', error);
      setLookups({
        consultants: [],
        sectors: [],
        countries: []
      });
    }
  };

  const loadLeads = async () => {
    try {
      setLoading(true);
      const response = await leadService.getNegotiationLeads({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters
      });
      
      let filteredLeads = response.data.data.leads;

      // Apply quick filters
      if (quickFilter === 'my') {
        filteredLeads = filteredLeads.filter(lead => 
          lead.consultant_name === currentUser.full_name
        );
      } else if (quickFilter === 'stale') {
        filteredLeads = filteredLeads.filter(lead => 
          lead.days_active > 60
        );
      } else if (quickFilter === 'week') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        filteredLeads = filteredLeads.filter(lead => 
          new Date(lead.date_contacted) >= oneWeekAgo
        );
      }
      
      setLeads(filteredLeads);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total,
        pages: response.data.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error loading Negotiation leads:', error);
      alert('Failed to load leads. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      
      // Get all leads (no pagination)
      const response = await leadService.getNegotiationLeads({
        page: 1,
        limit: 10000, // Get all
        search: searchTerm,
        ...filters
      });
      
      const allLeads = response.data.data.leads;
      
      // Apply quick filter if active
      let exportLeads = allLeads;
      if (quickFilter === 'my') {
        exportLeads = allLeads.filter(lead => 
          lead.consultant_name === currentUser.full_name
        );
      } else if (quickFilter === 'stale') {
        exportLeads = allLeads.filter(lead => lead.days_active > 60);
      } else if (quickFilter === 'week') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        exportLeads = allLeads.filter(lead => 
          new Date(lead.date_contacted) >= oneWeekAgo
        );
      }

      // Create CSV content
      const headers = [
        'Organisation',
        'Project',
        'Client Name',
        'Client Email',
        'Consultant',
        'Status',
        'Sector',
        'Country',
        'Days Active',
        'Estimated Revenue',
        'Date Contacted',
        'Next Follow-Up'
      ];

      const csvRows = [
        headers.join(','),
        ...exportLeads.map(lead => [
          `"${(lead.organisation_name || '').replace(/"/g, '""')}"`,
          `"${(lead.project_name || '').replace(/"/g, '""')}"`,
          `"${(lead.client_name || '').replace(/"/g, '""')}"`,
          `"${(lead.client_email || '').replace(/"/g, '""')}"`,
          `"${(lead.consultant_name || 'Unassigned').replace(/"/g, '""')}"`,
          `"${(lead.status_name || '').replace(/"/g, '""')}"`,
          `"${(lead.sector_name || '').replace(/"/g, '""')}"`,
          `"${(lead.country_name || '').replace(/"/g, '""')}"`,
          lead.days_active || 0,
          lead.estimated_revenue || 0,
          lead.date_contacted || '',
          lead.next_followup_date || ''
        ].join(','))
      ];

      const csvContent = csvRows.join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `negotiation-leads-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert(`✅ Exported ${exportLeads.length} leads to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      alert('❌ Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadLeads();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadLeads();
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({
      consultant_id: '',
      sector_id: '',
      country_id: ''
    });
    setSearchTerm('');
    setQuickFilter('all');
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => loadLeads(), 100);
  };

  const handleQuickFilter = (filter) => {
    setQuickFilter(filter);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadLeads();
  };

  const handleViewDetails = (leadId) => {
    navigate(`/admin/leads/view/${leadId}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getDaysActiveColor = (days) => {
    if (!days) return 'text-slate-400';
    if (days > 60) return 'text-red-400';
    if (days > 30) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getDaysActiveBadge = (days) => {
    if (!days) return { bg: 'bg-slate-800', text: 'text-slate-400', label: 'New' };
    if (days > 60) return { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Stale' };
    if (days > 30) return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Warning' };
    return { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Active' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;
  const staleLeadsCount = leads.filter(lead => lead.days_active > 60).length;

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 text-lg">Loading Negotiation leads...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white">Negotiation Leads</h1>
            </div>
            <p className="text-slate-400">
              {pagination.total} leads in early stages
              {activeFilterCount > 0 && ` • ${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} active`}
              {staleLeadsCount > 0 && ` • ${staleLeadsCount} stale (60+ days)`}
            </p>
            <p className="text-slate-500 text-sm mt-1">
              Negotiation • Contract review
            </p>
          </div>
          <div className="flex items-center gap-3">
            {staleLeadsCount > 0 && (
              <div className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-red-400 font-semibold text-sm">{staleLeadsCount} Stale</span>
              </div>
            )}
            <button
              onClick={handleExportCSV}
              disabled={exporting || leads.length === 0}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-bounce' : ''}`} />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-slate-400 text-sm font-medium">Quick Filters:</span>
          <button
            onClick={() => handleQuickFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              quickFilter === 'all'
                ? 'bg-green-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            All Leads
          </button>
          <button
            onClick={() => handleQuickFilter('my')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
              quickFilter === 'my'
                ? 'bg-green-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <UserIcon className="w-4 h-4" />
            My Leads
          </button>
          <button
            onClick={() => handleQuickFilter('stale')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
              quickFilter === 'stale'
                ? 'bg-red-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            Stale (60+ days)
          </button>
          <button
            onClick={() => handleQuickFilter('week')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
              quickFilter === 'week'
                ? 'bg-green-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <Calendar className="w-4 h-4" />
            This Week
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-slate-900 rounded-xl p-4 mb-6 border border-slate-800">
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search leads..."
                  className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                />
              </div>
            </form>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
                showFilters || activeFilterCount > 0
                  ? 'bg-green-500 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <Filter className="w-5 h-5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-white text-green-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg font-semibold transition-colors"
            >
              Search
            </button>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Consultant</label>
                  <select
                    name="consultant_id"
                    value={filters.consultant_id}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">All Consultants</option>
                    {lookups.consultants.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Sector</label>
                  <select
                    name="sector_id"
                    value={filters.sector_id}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">All Sectors</option>
                    {lookups.sectors.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Country</label>
                  <select
                    name="country_id"
                    value={filters.country_id}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">All Countries</option>
                    {lookups.countries.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-semibold text-sm transition-colors"
                >
                  Apply Filters
                </button>
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Leads Table */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800 border-b border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Organisation</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Client</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Consultant</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Days Active</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Revenue</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {leads.length > 0 ? (
                  leads.map((lead) => {
                    const badge = getDaysActiveBadge(lead.days_active);
                    const daysColor = getDaysActiveColor(lead.days_active);
                    
                    return (
                      <tr key={lead.lead_id} className="hover:bg-slate-800/50 transition-colors group">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-white font-medium">{lead.organisation_name}</p>
                            {lead.project_name && (
                              <p className="text-slate-500 text-sm">{lead.project_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-slate-300">{lead.client_name || 'N/A'}</p>
                            {lead.client_email && (
                              <p className="text-slate-500 text-xs">{lead.client_email}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-300">{lead.consultant_name || 'Unassigned'}</td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-semibold">
                            {lead.status_name}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className={`w-4 h-4 ${daysColor}`} />
                            <span className={`font-semibold ${daysColor}`}>{lead.days_active || 0} days</span>
                            <span className={`px-2 py-0.5 ${badge.bg} ${badge.text} rounded text-xs font-bold`}>
                              {badge.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                            <DollarSign className="w-4 h-4" />
                            {formatCurrency(lead.estimated_revenue || 0)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleViewDetails(lead.lead_id)}
                              className="px-3 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors flex items-center gap-2"
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-16">
                      <div className="text-center">
                        <Users className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                        <p className="text-slate-400 text-lg mb-2">No Negotiation leads</p>
                        <p className="text-slate-500 text-sm">
                          {quickFilter !== 'all' ? 'Try a different filter' : 'Leads in initial stages will appear here'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-slate-800 px-6 py-4 flex items-center justify-between border-t border-slate-700">
              <div className="text-sm text-slate-400">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} leads
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          pagination.page === page
                            ? 'bg-green-600 text-white'
                            : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}